import { batch, SignalObject, signalObject } from "omi";
import isEmpty from "lodash-es/isEmpty";

import { useValidations } from "@/pages/project/store/queries/useValidations";
import { LAST_VIEWED_PROJECT } from "@/globals/storageKeys";
import { removeFromLocalStorage, saveToLocalStorage } from "@/utils/localStorage";
import { getFromQueryString } from "@/utils/url";
import { Plan, Project, ProjectMetadataVisibilityEnum, ProjectStatus, Track } from "@/api";

import { FeatureSlice } from "../features";
import { UserSlice } from "../user";
import { useProjectReset } from "./mutations/projectReset";
import { createContentSlice, ProjectContentSlice } from "./slices/content";
import { createVariablesSlice, VariablesSlice } from "./slices/variables";

type Slice = {
  id: Project["id"];
  metadata: Project["metadata"];
  status: Project["status"];
  completedBy: Project["completedBy"];
  completions: Project["completions"];

  locked: boolean;
  hasUpdates: boolean;
};

type Actions = {
  getProject: () => Project;
  setProject: (project: Project) => void;
  isComplete: () => boolean;
  setStatus: (status: ProjectStatus) => void;
  getSelectedTrackId: () => Track["id"] | undefined;
  isProProject: () => boolean;
  isProProjectUnlockedForUser: () => boolean;
  setProProjectUnlocked: () => void;
  doesUserHaveAccess: () => boolean;
  resetProject: () => Promise<boolean>;
};

type Slices = {
  variables: VariablesSlice;
  content: ProjectContentSlice;
};

export type ProjectSlice = SignalObject<Slice> & Actions & Slices;

export function createProjectSlice(userSlice: UserSlice, featureSlice: FeatureSlice): ProjectSlice {
  const variablesSlice = createVariablesSlice([]);
  const contentSlice = createContentSlice();
  const validationSlice = useValidations();

  const initialState: Omit<Slice, "variables"> = {
    id: undefined,
    metadata: undefined,
    status: ProjectStatus.PROJECT_INCOMPLETE,
    locked: false,
    hasUpdates: false,
    completedBy: [],
    completions: 0,
  };

  const state = signalObject(initialState);

  contentSlice.setSelectedTrack(getInitialTrackSelector(state, userSlice, featureSlice)());

  // TODO later - Add reselect to memoize state selector functions
  return {
    ...state,
    variables: variablesSlice,
    content: contentSlice,

    getProject: getProjectSelector(state, variablesSlice),
    setProject: setProjectSelector(state, variablesSlice),
    getSelectedTrackId: getSelectedTrackIdSelector(contentSlice),
    isComplete: isCompleteSelector(state),
    setStatus: setStatusSelector(state),
    isProProject: isProProjectSelector(state),
    doesUserHaveAccess: doesUserHaveAccessSelector(state, userSlice, featureSlice),
    isProProjectUnlockedForUser: isProProjectUnlockedSelector(state, userSlice),
    setProProjectUnlocked: setProProjectUnlockedSelector(state, userSlice),
    resetProject: resetProjectSelector(state, userSlice, variablesSlice, contentSlice, validationSlice),
  };
}

function getProjectSelector(state: SignalObject<Slice>, variablesSlice: VariablesSlice): () => Project {
  return () => ({
    id: state.id.value,
    metadata: state.metadata.value,
    status: state.status.value,
    hasUpdates: state.hasUpdates.value,
    variables: variablesSlice.getAll(),
  });
}

function getSelectedTrackIdSelector(contentSlice: ProjectContentSlice): () => Track["id"] | undefined {
  return () => contentSlice.selectedTrackId.value;
}

function setStatusSelector(state: SignalObject<Slice>): (status: ProjectStatus) => void {
  return status => {
    state.status.value = status;
  };
}

function setProjectSelector(state: SignalObject<Slice>, variablesSlice: VariablesSlice): (project: Project) => void {
  return project => {
    if (project.id) {
      saveToLocalStorage(LAST_VIEWED_PROJECT, project.id);
    } else {
      removeFromLocalStorage(LAST_VIEWED_PROJECT);
    }

    batch(() => {
      state.id.value = project.id;
      state.metadata.value = project.metadata;
      state.status.value = project.status;
      state.locked.value = project.locked != null ? project.locked : true;
      state.hasUpdates.value = project.hasUpdates ?? false;

      variablesSlice.setAll(project.variables ?? []);

      if (project.completedBy) {
        state.completedBy.value = project.completedBy;
      }

      if (project.completions) {
        state.completions.value = project.completions;
      }
    });
  };
}

function isProProjectSelector(state: SignalObject<Slice>): () => boolean {
  return () => state.metadata.value?.plan === Plan.PRO;
}

function isCompleteSelector(state: SignalObject<Slice>): () => boolean {
  return () => state.status.value === ProjectStatus.PROJECT_COMPLETE;
}

function isProProjectUnlockedSelector(state: SignalObject<Slice>, userSlice: UserSlice): () => boolean {
  return () => {
    return userSlice.isPro() && state.locked.value == false;
  };
}

function setProProjectUnlockedSelector(state: SignalObject<Slice>, userSlice: UserSlice): () => void {
  return async () => {
    if (userSlice.isPro()) {
      state.locked.value = false;
    } else {
      console.error("Cannot set pro project unlocked for non-pro user");
    }
  };
}

function doesUserHaveAccessSelector(
  state: SignalObject<Slice>,
  userSlice: UserSlice,
  featureSlice: FeatureSlice,
): () => boolean {
  return () => {
    const isProProject = isProProjectSelector(state)();
    const isProUnlocked = isProProjectUnlockedSelector(state, userSlice)();
    const isUserAdmin = featureSlice.isAdmin;

    return isProProject
      ? isProUnlocked
      : isUserAdmin || state.metadata.value?.visibility === ProjectMetadataVisibilityEnum.PUBLIC;
  };
}

function getInitialTrackSelector(
  state: SignalObject<Slice>,
  userSlice: UserSlice,
  featureSlice: FeatureSlice,
): () => Required<Track["id"]> | undefined {
  return () => {
    const isProProject = isProProjectSelector(state)();
    const doesUserHaveAccess = doesUserHaveAccessSelector(state, userSlice, featureSlice)();
    const isProUnlocked = isProProjectUnlockedSelector(state, userSlice)();

    if (isProProject && (!doesUserHaveAccess || !isProUnlocked)) {
      getFromQueryString<string>("track", true);
      return undefined;
    }

    const selectedFromQueryString = getFromQueryString<string>("track");
    if (!isEmpty(selectedFromQueryString)) {
      return selectedFromQueryString;
    }

    return undefined;
  };
}

function resetProjectSelector(
  state: SignalObject<Slice>,
  user: UserSlice,
  variablesSlice: VariablesSlice,
  contentSlice: ProjectContentSlice,
  validationSlice: ReturnType<typeof useValidations>,
): () => Promise<boolean> {
  const { mutate: resetProject } = useProjectReset();
  const setProject = setProjectSelector(state, variablesSlice);

  return async () => {
    const projectId = state.id.value;

    if (!user.isPro() || isEmpty(projectId)) {
      return false;
    }

    const res = await resetProject(projectId!);

    if (res) {
      setProject(res);
      contentSlice.fetchTrackContent();
      contentSlice.fetchIntroContent();
      validationSlice.fetch(projectId!);
    }

    return true;
  };
}
